import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';

interface CreateQuoteModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

const CreateQuoteModal: React.FC<CreateQuoteModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
}) => {
    const [quoteTitle, setQuoteTitle] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        if (!isOpen) {
            setQuoteTitle('');
            setErrorMessage('');
            setSubmitting(false);
        }
    }, [isOpen]);

    if (!isOpen) {
        return null;
    }

    const closeButtonClassName = `selector-modal__close${submitting ? ' is-disabled' : ''}`;
    const secondaryButtonClassName = `button weka-secondary-btn selector-modal__button selector-modal__button--secondary${submitting ? ' is-disabled' : ''}`;
    const primaryButtonClassName = `button weka-primary-btn selector-modal__button selector-modal__button--primary${submitting ? ' is-disabled' : ''}`;

    const handleCreateQuote = async () => {
        const trimmedTitle = quoteTitle.trim();
        if (trimmedTitle === '') {
            setErrorMessage('Please enter a quote name.');
            return;
        }

        setSubmitting(true);
        setErrorMessage('');

        try {
            const response = await api.createQuote({
                title: trimmedTitle,
                status: 'pending',
            });

            api.invalidateCache('weka/v1/quotes');
            toast.success(response.message || 'Quote created successfully.');
            onSuccess?.();
            onClose();
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to create quote.';
            setErrorMessage(message);
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="selector-modal-overlay"
            onClick={() => {
                if (!submitting) {
                    onClose();
                }
            }}
        >
            <div
                className="selector-modal"
                onClick={(event) => event.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="create-quote-modal-title"
            >
                <div className="selector-modal__header">
                    <div>
                        <div id="create-quote-modal-title" className="selector-modal__title">Create Quote</div>
                        <p className="selector-modal__subtitle">
                            Enter a quote name to create a new empty quote. Products can be attached later from the product flow.
                        </p>
                    </div>
                    <button
                        type="button"
                        aria-label="Close"
                        disabled={submitting}
                        onClick={onClose}
                        className={closeButtonClassName}
                    >
                        &times;
                    </button>
                </div>

                <form
                    className="selector-modal__body"
                    onSubmit={(event) => {
                        event.preventDefault();
                        void handleCreateQuote();
                    }}
                >
                    {errorMessage !== '' && (
                        <div className="selector-modal__error" role="alert">
                            {errorMessage}
                        </div>
                    )}

                    <section className="selector-modal__section">
                        <div>
                            <div className="selector-modal__section-title">Quote Name</div>
                            <p className="selector-modal__section-hint">This name will be shown in your quote history table.</p>
                        </div>
                        <input
                            type="text"
                            placeholder="e.g., Warehouse Restock Quote"
                            value={quoteTitle}
                            onChange={(event) => setQuoteTitle(event.currentTarget.value)}
                            disabled={submitting}
                            className="selector-modal__input"
                            autoFocus
                        />
                    </section>

                    <div className="selector-modal__actions">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={submitting}
                            className={secondaryButtonClassName}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            className={primaryButtonClassName}
                        >
                            {submitting ? 'Creating...' : 'Create Quote'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateQuoteModal;
